import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, ArrowRight } from "lucide-react";
import { useLang } from "@/lib/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const { t } = useLang();
  const [form, setForm] = useState({
    name: "", company: "", email: "", phone: "", discipline: "", message: "", gdpr_consent: false,
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.gdpr_consent) {
      toast.error(t("contact.gdprError"));
      return;
    }
    if (!form.name || !form.email || !form.message) {
      toast.error(t("contact.requiredError"));
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success(t("contact.success"));
      setForm({ name: "", company: "", email: "", phone: "", discipline: "", message: "", gdpr_consent: false });
    } catch {
      toast.error(t("contact.error"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative py-14 lg:py-20 bg-[#F3F4F6]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Desktop-only centered top header */}
        <div className="hidden lg:block text-center max-w-4xl mx-auto mb-12">
          <span className="text-sm uppercase tracking-[0.25em] font-extrabold text-[#1E3A8A]">{t("contact.eyebrow")}</span>
          <h2 className="mt-6 font-heading font-bold text-[#0A192F] text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.1]">
            {t("contact.title")}
          </h2>
          <p className="mt-6 text-slate-600 leading-relaxed">{t("contact.lead")}</p>
        </div>

        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }} className="lg:col-span-5">
            <div className="lg:hidden text-center">
              <span className="text-sm uppercase tracking-[0.25em] font-extrabold text-[#1E3A8A]">{t("contact.eyebrow")}</span>
              <h2 className="mt-6 font-heading font-bold text-[#0A192F] text-3xl sm:text-4xl tracking-tight leading-[1.1]">
                {t("contact.title")}
              </h2>
              <p className="mt-6 text-slate-600 leading-relaxed">{t("contact.lead")}</p>
            </div>

            <div className="mt-10 lg:mt-0 grid gap-4">
              <div className="group bg-[#0A192F] p-6 rounded-xl border border-[#0A192F] hover:border-[#3B82F6]/70 hover:shadow-[0_0_30px_rgba(59,130,246,0.25)] transition-all duration-300">
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-full bg-white/10 border border-[#C9A96E]/30 flex items-center justify-center flex-shrink-0">
                    <MapPin size={18} className="text-[#C9A96E]" />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-[0.2em] text-[#C9A96E] font-semibold">{t("contact.address")}</div>
                    <div className="mt-2 text-white font-medium leading-relaxed">
                      X10 Management GmbH<br />Herner Str. 299<br />44809 Bochum
                    </div>
                  </div>
                </div>
              </div>

              <div className="group bg-[#0A192F] p-6 rounded-xl border border-[#0A192F] hover:border-[#3B82F6]/70 hover:shadow-[0_0_30px_rgba(59,130,246,0.25)] transition-all duration-300">
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-full bg-white/10 border border-[#C9A96E]/30 flex items-center justify-center flex-shrink-0">
                    <Mail size={18} className="text-[#C9A96E]" />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-[0.2em] text-[#C9A96E] font-semibold">{t("contact.email")}</div>
                    <a href="mailto:kontakt@x10management.de" className="mt-2 block text-white font-medium hover:text-[#93C5FD] transition-colors">
                      kontakt@x10management.de
                    </a>
                  </div>
                </div>
              </div>

              <div className="group bg-[#0A192F] p-6 rounded-xl border border-[#0A192F] hover:border-[#3B82F6]/70 hover:shadow-[0_0_30px_rgba(59,130,246,0.25)] transition-all duration-300">
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-full bg-white/10 border border-[#C9A96E]/30 flex items-center justify-center flex-shrink-0">
                    <Phone size={18} className="text-[#C9A96E]" />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-[0.2em] text-[#C9A96E] font-semibold">{t("contact.phone")}</div>
                    <a href="tel:+4923460550" className="mt-2 block text-white font-medium hover:text-[#93C5FD] transition-colors">
                      +49 234 6055 0
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.form
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.1 }}
            onSubmit={submit}
            data-testid="contact-form"
            className="lg:col-span-6 lg:col-start-7 bg-white rounded-2xl border border-slate-200 p-8 lg:p-10 self-start"
          >
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label={t("contact.labels.name")}>
                <input type="text" value={form.name} onChange={update("name")} data-testid="contact-input-name" className="w-full bg-transparent border-b border-slate-300 py-3 text-slate-900 focus:border-[#0A192F] focus:outline-none transition-colors" required />
              </Field>
              <Field label={t("contact.labels.company")}>
                <input type="text" value={form.company} onChange={update("company")} data-testid="contact-input-company" className="w-full bg-transparent border-b border-slate-300 py-3 text-slate-900 focus:border-[#0A192F] focus:outline-none transition-colors" />
              </Field>
              <Field label={t("contact.labels.emailField")}>
                <input type="email" value={form.email} onChange={update("email")} data-testid="contact-input-email" className="w-full bg-transparent border-b border-slate-300 py-3 text-slate-900 focus:border-[#0A192F] focus:outline-none transition-colors" required />
              </Field>
              <Field label={t("contact.labels.phoneField")}>
                <input type="tel" value={form.phone} onChange={update("phone")} data-testid="contact-input-phone" className="w-full bg-transparent border-b border-slate-300 py-3 text-slate-900 focus:border-[#0A192F] focus:outline-none transition-colors" />
              </Field>
            </div>

            <div className="mt-6">
              <Field label={t("contact.labels.discipline")}>
                <select
                  value={form.discipline}
                  onChange={update("discipline")}
                  data-testid="contact-input-discipline"
                  className="w-full bg-transparent border-b border-slate-300 py-3 text-slate-900 focus:border-[#0A192F] focus:outline-none transition-colors cursor-pointer"
                >
                  <option value="">{t("contact.disciplineOptions.none")}</option>
                  <option value="process">{t("contact.disciplineOptions.process")}</option>
                  <option value="supply_chain">{t("contact.disciplineOptions.supply_chain")}</option>
                  <option value="industrial_ai">{t("contact.disciplineOptions.industrial_ai")}</option>
                  <option value="ai_robotics">{t("contact.disciplineOptions.ai_robotics")}</option>
                  <option value="other">{t("contact.disciplineOptions.other")}</option>
                </select>
              </Field>
            </div>

            <div className="mt-6">
              <Field label={t("contact.labels.message")}>
                <textarea rows={5} value={form.message} onChange={update("message")} data-testid="contact-input-message" className="w-full bg-transparent border-b border-slate-300 py-3 text-slate-900 focus:border-[#0A192F] focus:outline-none transition-colors resize-none" required />
              </Field>
            </div>

            <label className="mt-8 flex items-start gap-3 text-sm text-slate-600 cursor-pointer" data-testid="contact-gdpr-label">
              <input type="checkbox" checked={form.gdpr_consent} onChange={update("gdpr_consent")} data-testid="contact-input-gdpr" className="mt-1 w-4 h-4 accent-[#0A192F] cursor-pointer" />
              <span>
                {t("contact.gdpr")}
                <Link to="/datenschutz" className="underline hover:text-[#0A192F]">{t("contact.gdprLink")}</Link>.
              </span>
            </label>

            <button
              type="submit"
              disabled={submitting}
              data-testid="contact-submit-button"
              className="mt-10 group inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#0A192F] text-white font-semibold rounded-full hover:bg-[#1E3A8A] transition-all duration-300 disabled:opacity-60"
            >
              {submitting ? t("contact.sending") : t("contact.submit")}
              <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
            </button>
          </motion.form>
        </div>
      </div>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#1E3A8A]">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
